/**
 * Shared pricing configuration for display across landing, upgrade, and settings pages.
 * Annual pricing gives 2 months free (pay for 10 months instead of 12).
 */

export const PRICING = {
  FREE: {
    monthly: 0,
    annual: 0,
  },
  STARTER: {
    monthly: 19.95,
    annual: 199.50, // $19.95 * 10 = 2 months free
  },
  PREMIUM: {
    monthly: 29.95,
    annual: 299.50, // $29.95 * 10 = 2 months free
  },
} as const;

export type BillingInterval = "monthly" | "annual";

/**
 * Format a price number for display (e.g. 19.95 → "$19.95", 0 → "$0")
 */
export function formatDisplayPrice(price: number): string {
  if (price === 0) return "$0";
  return `$${price.toFixed(2)}`;
}

/**
 * Get the effective monthly price for a given interval
 */
export function getEffectiveMonthlyPrice(plan: keyof typeof PRICING, interval: BillingInterval): number {
  if (interval === "monthly") return PRICING[plan].monthly;
  return PRICING[plan].annual / 12;
}

/**
 * Get the annual savings amount
 */
export function getAnnualSavings(plan: keyof typeof PRICING): number {
  return (PRICING[plan].monthly * 12) - PRICING[plan].annual;
}

/**
 * Get the annual savings as a percentage
 */
export function getAnnualSavingsPercent(plan: keyof typeof PRICING): number {
  if (PRICING[plan].monthly === 0) return 0;
  const totalMonthly = PRICING[plan].monthly * 12;
  return Math.round(((totalMonthly - PRICING[plan].annual) / totalMonthly) * 100);
}
